import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const MAX_RETRIES = 40;
// Images and web fonts below/inside the target section are often still
// loading when we first find it, so its final resting position isn't known
// yet — a single scroll fired at that moment can land short or long
// depending on what's cached. These re-checks correct the position shortly
// after, once that late layout settling has actually happened.
const CORRECTION_DELAYS_MS = [350, 900];

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = hash.slice(1);
    let attempts = 0;
    let frame: number;
    const timers: number[] = [];

    const scrollToTarget = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const header = document.querySelector("header");
      const offset = (header?.getBoundingClientRect().height ?? 0) + 16;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    };

    // A nav link to e.g. "/#about" clicked from a different page lands here
    // before HomePage (and its sections) have actually mounted, so the
    // target element doesn't exist yet on the first render — retry across a
    // few frames instead of giving up and leaving the visitor at the top.
    const tryScroll = () => {
      if (document.getElementById(id)) {
        scrollToTarget();
        for (const delay of CORRECTION_DELAYS_MS) {
          timers.push(window.setTimeout(scrollToTarget, delay));
        }
      } else if (attempts++ < MAX_RETRIES) {
        frame = requestAnimationFrame(tryScroll);
      }
    };
    tryScroll();
    return () => {
      cancelAnimationFrame(frame);
      timers.forEach(clearTimeout);
    };
  }, [pathname, hash]);

  return null;
}
